<template>
  <section class="ar-waiting" aria-labelledby="ar-waiting-title" v-if="rows.length">
    <div class="ar-waiting__head">
      <h2 id="ar-waiting-title" class="ar-waiting__title">Waiting for you</h2>
      <span class="ar-waiting__count">{{ rows.length }}</span>
      <span class="ar-waiting__hint">Prepared by AI, waiting for your approval</span>
    </div>
    <ul class="ar-waiting__list">
      <li v-for="row in rows" :key="row.requestId">
        <router-link class="ar-waiting__row" :to="{ name: 'approval-request', params: { id: row.requestId } }">
          <span class="ar-waiting__what">
            <span class="ar-waiting__module">{{ row.module }}</span>
            <span class="ar-waiting__entry">{{ row.activity }} · {{ row.entryTitle || 'New entry' }}</span>
          </span>
          <span class="ar-waiting__flow" v-if="row.fromState || (row.nextStates && row.nextStates.length)">
            <span class="ar-waiting__state">{{ row.fromState || 'New' }}</span>
            <span aria-hidden="true">→</span>
            <span class="ar-waiting__state ar-waiting__state--to">{{ (row.nextStates || []).join(' or ') || 'same' }}</span>
          </span>
          <span class="ar-waiting__who">
            <span class="ar-waiting__ai" aria-hidden="true">AI</span>
            {{ who(row) }}
          </span>
          <span class="ar-waiting__review">Review</span>
        </router-link>
      </li>
    </ul>
  </section>
</template>

<script>
import { listApprovalRequests } from './approvalApi';

/**
 * "Waiting for you" on the dashboard (docs/teammate-human-approval-request-proposal.md §6): every
 * open approval request for the signed-in person in this workspace, across modules, one row each.
 * Hidden while there are none; refreshed whenever a notification arrives.
 */
export default {
  name: 'FTSApprovalWaitingList',
  data() {
    return { rows: [] };
  },
  mounted() {
    this.load();
    window.eventBus.$on('refreshNotification', this.load);
  },
  beforeDestroy() {
    window.eventBus.$off('refreshNotification', this.load);
  },
  methods: {
    async load() {
      try {
        const res = await listApprovalRequests('pending');
        this.rows = (res.data && res.data.list) || [];
      } catch (err) {
        // A server without approval requests (or a caller it refuses) simply shows nothing here.
        this.rows = [];
      }
    },
    who(row) {
      const by = row.requestedBy || {};
      const agent = by.agent || 'AI agent';
      const forWhom = by.displayName || by.username;
      return forWhom ? `${agent} for ${forWhom}` : agent;
    },
  },
};
</script>

<style lang="scss" scoped>
.ar-waiting {
  background: var(--inistate-color-surface, #fff);
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  margin-bottom: 1rem;
}
.ar-waiting__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #eee;
}
.ar-waiting__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
}
.ar-waiting__count {
  background: #1e6f8b;
  color: #fff;
  border-radius: 999px;
  font-size: 0.75rem;
  padding: 0.0625rem 0.5rem;
}
.ar-waiting__hint {
  margin-left: auto;
  font-size: 0.8125rem;
  color: #616161;
}
.ar-waiting__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ar-waiting__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #f1f1f1;
  color: inherit;
  text-decoration: none;
  &:hover { background: #f7fbfd; }
}
li:first-child .ar-waiting__row { border-top: 0; }
.ar-waiting__what {
  display: flex;
  flex-direction: column;
  min-width: 12rem;
  flex: 1 1 14rem;
}
.ar-waiting__module {
  font-size: 0.75rem;
  color: #616161;
}
.ar-waiting__entry {
  font-weight: 500;
}
.ar-waiting__flow {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
}
.ar-waiting__state {
  background: #eee;
  border-radius: 4px;
  padding: 0.125rem 0.375rem;
}
.ar-waiting__state--to {
  background: #e6f4fa;
  color: #1e6f8b;
  font-weight: 500;
}
.ar-waiting__who {
  font-size: 0.8125rem;
  color: #424242;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}
.ar-waiting__ai {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #1e6f8b;
  background: #e6f4fa;
  border-radius: 4px;
  padding: 0.0625rem 0.3125rem;
}
.ar-waiting__review {
  margin-left: auto;
  font-weight: 500;
  color: #1e6f8b;
  border: 1px solid #1e6f8b;
  border-radius: 4px;
  padding: 0.375rem 0.875rem;
}
</style>
